import { useSelector } from 'react-redux';
import { Panel } from 'modules/experiments/components/ui';
import { PANEL_HEIGHT, dataTestId } from 'modules/experiments/constants';
import {
  VariantCardContextProvider,
  VariantCardFormProvider,
  useTakeoverExperimentContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import {
  useExperimentsTranslations,
  useExperimentsUserPermission,
} from 'modules/experiments/hooks';
import { getTakeoverCampaignMessageMedium } from 'modules/experiments/ducks/selectors';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Add16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { PanelAction, PanelBody } from './Styled';
import { VariantCard } from './variant_card';

type VariantConfigurationProps = {
  loading: boolean;
};

export const VariantConfiguration = ({ loading }: VariantConfigurationProps) => {
  const { t } = useExperimentsTranslations();
  const campaignMessageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const { viewOnlyPermission } = useExperimentsUserPermission(campaignMessageMedium);
  const { isSTOExperiment, isRunningExperiment } = useTakeoverExperimentContext();
  const {
    isPreviewSelectMode,
    variantList,
    addingNewVariant,
    hasMaximumNumberOfVariants,
    addNewVariant,
  } = useVariantConfigurationContext();

  return (
    <Panel
      testId={dataTestId.variantConfigurationSection}
      title={t('panelHeading.variantSetup')}
      height={PANEL_HEIGHT.VARIANT_SETUP}
      loading={loading}
    >
      <PanelBody>
        {variantList.map((variant, cardIndex) => (
          <VariantCardFormProvider key={variant.id} variant={variant}>
            <VariantCardContextProvider cardIndex={cardIndex} variant={variant}>
              <VariantCard />
            </VariantCardContextProvider>
          </VariantCardFormProvider>
        ))}
        {isPreviewSelectMode && !isSTOExperiment && !isRunningExperiment && !viewOnlyPermission && (
          <PanelAction data-test={dataTestId.variantConfigurationActionButtons}>
            <Tooltip
              isEnabled={hasMaximumNumberOfVariants}
              tooltipContent={t('variantCard.tooltip.disableVariantCreation')}
            >
              <SecondaryButton
                size={BUTTON_SIZES.SMALL}
                isDisabled={addingNewVariant || hasMaximumNumberOfVariants}
                isLoading={addingNewVariant}
                onClick={addNewVariant}
              >
                <Add16 /> {t('button.addVariant')}
              </SecondaryButton>
            </Tooltip>
          </PanelAction>
        )}
      </PanelBody>
    </Panel>
  );
};
